import React from 'react';
import { Home, FlaskConical, Users, Menu, X, Book, Search, CheckSquare, MoreHorizontal, Mail, HelpCircle, Network, FileText, HeartHandshake, Target, Brain, Beaker, Eye, Cog, MessageCircle, Library, Puzzle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Logo } from '../common/Logo';
import { SearchInput } from '../search/SearchInput';
import { useNavigate } from 'react-router-dom';
import type { CognitiveBias } from '../../types';
import { Portal } from '../ui/Portal';
import { AccentButton } from '../ui/buttons/AccentButton';

interface NavbarProps {
  biases: CognitiveBias[];
  isLoading?: boolean;
}

const NavbarSkeleton = () => (
  <div className="flex items-center gap-2">
    {/* Logo skeleton */}
    <div className="w-8 h-8 rounded-full bg-orange-200/50 animate-pulse shrink-0" />
    
    {/* Title skeleton */}
    <div className="hidden sm:block w-40 h-6 bg-orange-200/50 animate-pulse rounded-md" />
    
    {/* Search bar skeleton */}
    <div className="flex-1 max-w-md ml-4 hidden md:block">
      <div className="w-full h-10 bg-orange-200/50 animate-pulse rounded-lg" />
    </div>
    
    {/* Nav items skeleton */}
    <div className="hidden md:flex gap-6 ml-auto">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-md bg-orange-200/50 animate-pulse" />
          <div className="w-16 h-5 bg-orange-200/50 animate-pulse rounded-md" />
        </div>
      ))}
    </div>
    
    {/* Mobile nav items skeleton */}
    <div className="md:hidden flex justify-between w-full ml-auto">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="w-10 h-10 bg-orange-200/50 animate-pulse rounded-lg" />
      ))}
    </div>
  </div>
);

export const Navbar: React.FC<NavbarProps> = ({ biases }) => {
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [isMobileMoreMenuOpen, setIsMobileMoreMenuOpen] = React.useState(false);
  const [isDesktopMoreMenuOpen, setIsDesktopMoreMenuOpen] = React.useState(false);
  const [isLearnDropdownOpen, setIsLearnDropdownOpen] = React.useState(false);
  const [isPracticeDropdownOpen, setIsPracticeDropdownOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState(' '); // Start with a space to trigger suggestions
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [mobileMenuPosition, setMobileMenuPosition] = React.useState({ top: 0, right: 0, width: 0 });
  const [desktopMenuPosition, setDesktopMenuPosition] = React.useState({ top: 0, left: 0, width: 0 });
  const [learnDropdownPosition, setLearnDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 });
  const [practiceDropdownPosition, setPracticeDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 });
  const location = useLocation();
  const navigate = useNavigate();
  const isLoading = !biases || biases.length === 0;
  const searchRef = React.useRef<HTMLDivElement>(null);
  const mobileMoreMenuRef = React.useRef<HTMLDivElement>(null);
  const mobileMoreButtonRef = React.useRef<HTMLButtonElement>(null);
  const desktopMoreMenuRef = React.useRef<HTMLDivElement>(null);
  const desktopMoreButtonRef = React.useRef<HTMLButtonElement>(null);
  const learnButtonRef = React.useRef<HTMLAnchorElement>(null);
  const practiceButtonRef = React.useRef<HTMLAnchorElement>(null);
  const learnDropdownRef = React.useRef<HTMLDivElement>(null);
  const practiceDropdownRef = React.useRef<HTMLDivElement>(null);

  // Check if current page is a bias page
  const isBiasPage = location.pathname.startsWith('/bias/');
  
  // Check if current page is home page
  const isHomePage = location.pathname === '/';
  
  // Check if current page is a practice page
  const isPracticePage = location.pathname.startsWith('/practice/');
  
  // Check if current page is the quiz page
  const isQuizPage = location.pathname.startsWith('/quiz');

  // Check if current page is the pre-mortem page
  const isPreMortemPage = location.pathname.startsWith('/pre-mortem');
  
  // Check if current page is the biases list page
  const isBiasesListPage = location.pathname === '/biases/' || location.pathname === '/biases';
  
  // Should use sticky navbar?
  const useStickyNavbar = !isBiasPage && !isPracticePage && !isQuizPage && !isPreMortemPage && !isBiasesListPage;

  // Function to update mobile menu position
  const updateMobileMenuPosition = () => {
    if (mobileMoreButtonRef.current) {
      const rect = mobileMoreButtonRef.current.getBoundingClientRect();
      setMobileMenuPosition({
        top: rect.bottom + 8,
        right: 16,
        width: 180 // Fixed width for mobile menu
      });
    }
  };

  // Function to update desktop menu position
  const updateDesktopMenuPosition = () => {
    if (desktopMoreButtonRef.current) {
      const rect = desktopMoreButtonRef.current.getBoundingClientRect();
      setDesktopMenuPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: 200 // Fixed width for desktop menu
      });
    }
  };

  // Add position update functions for new dropdowns
  const updateLearnDropdownPosition = () => {
    if (learnButtonRef.current) {
      const rect = learnButtonRef.current.getBoundingClientRect();
      setLearnDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: 250 // Fixed width for dropdown menu
      });
    }
  };

  const updatePracticeDropdownPosition = () => {
    if (practiceButtonRef.current) {
      const rect = practiceButtonRef.current.getBoundingClientRect();
      setPracticeDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: 250 // Fixed width for dropdown menu
      });
    }
  };

  // Close all dropdowns on location change
  React.useEffect(() => {
    setIsSearchOpen(false);
    setIsMobileMoreMenuOpen(false);
    setIsDesktopMoreMenuOpen(false);
    setIsLearnDropdownOpen(false);
    setIsPracticeDropdownOpen(false);
  }, [location]);

  // Close search when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
      
      if (
        mobileMoreMenuRef.current && 
        mobileMoreButtonRef.current && 
        !mobileMoreMenuRef.current.contains(event.target as Node) && 
        !mobileMoreButtonRef.current.contains(event.target as Node)
      ) {
        setIsMobileMoreMenuOpen(false);
      }
      
      if (
        desktopMoreMenuRef.current && 
        desktopMoreButtonRef.current && 
        !desktopMoreMenuRef.current.contains(event.target as Node) && 
        !desktopMoreButtonRef.current.contains(event.target as Node)
      ) {
        setIsDesktopMoreMenuOpen(false);
      }

      if (
        learnDropdownRef.current && 
        learnButtonRef.current && 
        !learnDropdownRef.current.contains(event.target as Node) && 
        !learnButtonRef.current.contains(event.target as Node)
      ) {
        setIsLearnDropdownOpen(false);
      }

      if (
        practiceDropdownRef.current && 
        practiceButtonRef.current && 
        !practiceDropdownRef.current.contains(event.target as Node) && 
        !practiceButtonRef.current.contains(event.target as Node)
      ) {
        setIsPracticeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Detect scroll for navbar styling, but only if not on a bias page, practice page, or quiz page
  React.useEffect(() => {
    if (isBiasPage || isPracticePage || isQuizPage) {
      setIsScrolled(false);
      return;
    }
    
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isBiasPage, isPracticePage, isQuizPage]);

  // Handle mobile menu position updates
  React.useEffect(() => {
    if (isMobileMoreMenuOpen) {
      // Initial position update
      updateMobileMenuPosition();
      
      // Update on scroll and resize
      const handleUpdate = () => {
        updateMobileMenuPosition();
      };
      
      window.addEventListener('scroll', handleUpdate, { passive: true });
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [isMobileMoreMenuOpen]);

  // Handle desktop menu position updates
  React.useEffect(() => {
    if (isDesktopMoreMenuOpen) {
      // Initial position update
      updateDesktopMenuPosition();
      
      // Update on scroll and resize
      const handleUpdate = () => {
        updateDesktopMenuPosition();
      };
      
      window.addEventListener('scroll', handleUpdate, { passive: true });
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [isDesktopMoreMenuOpen]);

  // Add effects for new dropdown position updates
  React.useEffect(() => {
    if (isLearnDropdownOpen) {
      updateLearnDropdownPosition();
      
      const handleUpdate = () => {
        updateLearnDropdownPosition();
      };
      
      window.addEventListener('scroll', handleUpdate, { passive: true });
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [isLearnDropdownOpen]);

  React.useEffect(() => {
    if (isPracticeDropdownOpen) {
      updatePracticeDropdownPosition();
      
      const handleUpdate = () => {
        updatePracticeDropdownPosition();
      };
      
      window.addEventListener('scroll', handleUpdate, { passive: true });
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [isPracticeDropdownOpen]);

  // Reset search term when closing search
  React.useEffect(() => {
    if (!isSearchOpen) {
      setSearchTerm('');
    } else {
      // Set a space and then clear it to trigger suggestions
      setSearchTerm(' ');
      setTimeout(() => setSearchTerm(''), 100);
    }
  }, [isSearchOpen]);

  const handleBiasSelect = (bias: CognitiveBias) => {
    navigate(`/bias/${bias.id}/`);
    setIsSearchOpen(false);
  };

  const navItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Book, label: 'Learn', path: '/learn/' },
    { icon: FlaskConical, label: 'Practice', path: '/practice/' },
  ] as const;
  
  interface MenuItem {
    icon: typeof FileText; // Using a LucideIcon type to ensure compatibility
    label: string;
    path: string;
    highlight?: boolean;
  }
  
  const moreMenuItems: MenuItem[] = [
    { icon: HeartHandshake, label: 'Support Us', path: '/support/', highlight: true },
    { icon: FileText, label: 'Blog', path: '/blog/' },
    { icon: Target, label: 'Pre-Mortem', path: '/pre-mortem/' },
    { icon: CheckSquare, label: 'Quiz', path: '/quiz/' },
    { icon: Network, label: 'Network', path: '/network/' },
    { icon: Users, label: 'About', path: '/about/' },
    { icon: Mail, label: 'Contact', path: '/contact/' },
  ];

  // Define submenu items for each dropdown
  const learnMenuItems: MenuItem[] = [
    { icon: Book, label: 'See All Categories', path: '/learn/', highlight: true },
    { icon: FileText, label: 'See All Biases', path: '/biases/' },
    { icon: Brain, label: 'Memory Biases', path: '/category/memory/' },
    { icon: Beaker, label: 'Decision-Making Biases', path: '/category/decision-making/' },
    { icon: Users, label: 'Social Biases', path: '/category/social/' },
    { icon: Eye, label: 'Perceptual Biases', path: '/category/perceptual/' },
    { icon: Cog, label: 'Reasoning Biases', path: '/category/reasoning/' },
  ];

  const practiceMenuItems: MenuItem[] = [
    { icon: FlaskConical, label: 'See All Practice Modes', path: '/practice/', highlight: true },
    { icon: MessageCircle, label: 'AI Cognitive Bias Coach', path: '/practice/role-play-debias/' },
    { icon: Brain, label: 'Interactive Tests', path: '/practice/test/' },
    { icon: Library, label: 'Flipcards', path: '/practice/flipcards/' },
    { icon: Puzzle, label: 'Match Game', path: '/practice/match/' },
  ];

  const toggleMobileMoreMenu = () => {
    setIsMobileMoreMenuOpen(!isMobileMoreMenuOpen);
    setIsDesktopMoreMenuOpen(false); // Close desktop menu when opening mobile menu
  };

  const toggleDesktopMoreMenu = () => {
    setIsDesktopMoreMenuOpen(!isDesktopMoreMenuOpen);
    setIsMobileMoreMenuOpen(false); // Close mobile menu when opening desktop menu
    // Close other dropdowns when opening more menu
    setIsLearnDropdownOpen(false);
    setIsPracticeDropdownOpen(false);
  };

  return (
    <nav className={`${useStickyNavbar ? 'sticky top-0 left-0 right-0' : 'relative'} z-40 transition-all duration-300
      ${isScrolled 
        ? 'bg-white/95 backdrop-blur-sm shadow-md border-b border-orange-200/50' 
        : isHomePage 
          ? 'bg-transparent' 
          : 'bg-gradient-to-r from-orange-50 via-orange-100/70 to-orange-50 shadow-sm border-b border-orange-200/30'}`}>
      {/* Background elements for home page */}
      {isHomePage && !isScrolled && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-orange-50 via-pink-50/50 to-orange-50/30" />
          <div className="absolute -top-20 -left-32 w-[400px] h-[400px] bg-gradient-to-br from-orange-200/40 to-pink-200/40 rounded-full mix-blend-multiply filter blur-[60px] animate-float transform-gpu" />
          <div className="absolute -top-40 right-0 w-[300px] h-[300px] bg-gradient-to-br from-pink-200/40 to-orange-200/40 rounded-full mix-blend-multiply filter blur-[40px] animate-float-reverse transform-gpu" />
        </div>
      )}

      <div className="container mx-auto relative z-10">
        {!isSearchOpen && (
          <div className="flex items-center justify-between py-4 px-4">
            {/* Mobile Navigation Container */}
            <div className="flex items-center justify-between w-full">
              <div className="md:hidden grid grid-cols-5 w-full gap-x-2">
                {/* Logo */}
                <Link 
                  to="/"
                  className="flex items-center justify-center"
                >
                  <Logo className="w-8 h-8 transition-transform hover:scale-105" />
                </Link>

                {/* Navigation Items - Distributed evenly */}
                <div className="col-span-4 flex justify-between items-center px-1">
                  {/* Search Icon */}
                  <button 
                    onClick={() => setIsSearchOpen(true)}
                    className="p-2 hover:bg-orange-200/50 rounded-lg transition-colors flex items-center justify-center"
                    aria-label="Search"
                  >
                    <Search size={20} />
                  </button>
                  
                  {/* Mobile Navigation Items */}
                  {navItems.map(({ icon: Icon, path }) => (
                    <Link
                      key={path}
                      to={path}
                      className={`p-2 rounded-lg transition-colors flex items-center justify-center
                        ${location.pathname === path 
                          ? 'bg-orange-200/50 text-orange-600' 
                          : 'hover:bg-orange-200/30 text-gray-700'}`}
                      aria-label={path === '/' ? 'Home' : path.slice(1)}
                    >
                      <Icon size={20} />
                    </Link>
                  ))}
                  
                  {/* Mobile More Menu Button */}
                  <div className="relative flex items-center justify-center">
                    <button
                      ref={mobileMoreButtonRef}
                      onClick={toggleMobileMoreMenu}
                      className={`p-2 rounded-lg transition-colors flex items-center justify-center
                        ${isMobileMoreMenuOpen 
                          ? 'bg-orange-200/50 text-orange-600' 
                          : 'hover:bg-orange-200/30 text-gray-700'}`}
                      aria-label="More options"
                      aria-expanded={isMobileMoreMenuOpen}
                    >
                      <MoreHorizontal size={20} />
                    </button>
                    
                    {/* Mobile More Menu Dropdown */}
                    {isMobileMoreMenuOpen && (
                      <Portal>
                        <div 
                          ref={mobileMoreMenuRef}
                          className="fixed bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-xl overflow-hidden z-[9999]"
                          style={{
                            width: `${mobileMenuPosition.width}px`,
                            maxWidth: 'calc(100vw - 32px)',
                            maxHeight: 'calc(100vh - 200px)',
                            overflowY: 'auto',
                            top: `${mobileMenuPosition.top}px`,
                            left: 'auto',
                            right: `${mobileMenuPosition.right}px`
                          }}
                        >
                          {/* Support Us First */}
                          <Link
                            to="/support/"
                            className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 active:bg-orange-100 cursor-pointer transition-colors duration-200 text-orange-500 font-semibold"
                            onClick={() => setIsMobileMoreMenuOpen(false)}
                          >
                            <HeartHandshake size={18} className="text-orange-500" />
                            <span>Support Us</span>
                          </Link>
                          
                          {/* Other Menu Items */}
                          {moreMenuItems.filter(item => item.path !== '/support/').map(({ icon: Icon, label, path, highlight }) => (
                            <Link
                              key={path}
                              to={path}
                              className={`flex items-center gap-3 px-4 py-3 hover:bg-orange-50 active:bg-orange-100 cursor-pointer transition-colors duration-200
                                ${location.pathname === path ? 'text-orange-600 bg-orange-50' : highlight ? 'text-orange-500 font-semibold' : 'text-gray-700'}`}
                              onClick={() => setIsMobileMoreMenuOpen(false)}
                            >
                              <Icon size={18} className={highlight ? 'text-orange-500' : ''} />
                              <span>{highlight ? label : label}</span>
                            </Link>
                          ))}
                        </div>
                      </Portal>
                    )}
                  </div>
                </div>
              </div>

              {/* Desktop version */}
              <div className="hidden md:flex items-center w-full">
                <Link 
                  to="/"
                  className="flex items-center gap-2 shrink-0 group"
                >
                  <Logo className="w-8 h-8 transition-transform hover:scale-105" />
                  <span className="font-semibold bg-gradient-to-r from-orange-600 via-pink-600 to-red-600 bg-clip-text text-transparent text-lg transition-transform group-hover:scale-[1.02]">
                    Cognitive Bias Lab
                  </span>
                </Link>

                <div className="flex-1 max-w-xs md:max-w-sm ml-4">
                  {isLoading ? (
                    <div className="w-full h-10 bg-orange-100/30 animate-pulse rounded-lg" />
                  ) : (
                    <SearchInput 
                      biases={biases} 
                      onBiasSelect={handleBiasSelect}
                    />
                  )}
                </div>

                <ul className="flex gap-8 ml-auto items-center">
                  {navItems.map(({ icon: Icon, label, path }, index) => (
                    <li key={path} className="relative">
                      <Link
                        ref={index === 1 ? learnButtonRef : index === 2 ? practiceButtonRef : null}
                        to={path}
                        className={`flex items-center gap-2 transition-all duration-300 hover:text-orange-600 font-medium relative group
                          ${location.pathname === path 
                            ? 'text-orange-600 scale-105' 
                            : isHomePage && !isScrolled ? 'text-gray-800' : 'text-gray-700'}`}
                        onMouseEnter={() => {
                          // Only handle hover on desktop and only for Learn and Practice
                          if (window.innerWidth >= 768) {
                            if (index === 1) setIsLearnDropdownOpen(true);
                            else if (index === 2) setIsPracticeDropdownOpen(true);
                            
                            // Close other dropdowns
                            if (index !== 1) setIsLearnDropdownOpen(false);
                            if (index !== 2) setIsPracticeDropdownOpen(false);
                            setIsDesktopMoreMenuOpen(false);
                          }
                        }}
                      >
                        <Icon size={20} className={location.pathname === path ? 'text-orange-600' : ''} /> 
                        {label}
                        <span className={`absolute -bottom-2 left-0 w-full h-0.5 bg-gradient-to-r from-orange-400 to-red-400 transform scale-x-0 transition-transform group-hover:scale-x-100 ${location.pathname === path ? 'scale-x-100' : ''}`}></span>
                      </Link>

                      {/* Learn Dropdown */}
                      {index === 1 && isLearnDropdownOpen && (
                        <Portal>
                          <div 
                            ref={learnDropdownRef}
                            className="fixed bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-xl overflow-hidden z-[9999]"
                            style={{
                              width: `${learnDropdownPosition.width}px`,
                              maxHeight: 'calc(100vh - 200px)',
                              overflowY: 'auto',
                              top: `${learnDropdownPosition.top}px`,
                              left: `${learnDropdownPosition.left}px`,
                              right: 'auto'
                            }}
                            onMouseLeave={() => setIsLearnDropdownOpen(false)}
                          >
                            {learnMenuItems.map(({ icon: MenuIcon, label: menuLabel, path: menuPath, highlight }) => (
                              <Link
                                key={menuPath}
                                to={menuPath}
                                className={`flex items-center gap-3 px-4 py-3 hover:bg-orange-50 active:bg-orange-100 cursor-pointer transition-colors duration-200
                                  ${location.pathname === menuPath ? 'text-orange-600 bg-orange-50' : highlight ? 'text-orange-500 font-semibold' : 'text-gray-700'}`}
                                onClick={() => setIsLearnDropdownOpen(false)}
                              >
                                <MenuIcon size={18} className={highlight ? 'text-orange-500' : ''} />
                                <span>{highlight ? menuLabel : menuLabel}</span>
                              </Link>
                            ))}
                          </div>
                        </Portal>
                      )}

                      {/* Practice Dropdown */}
                      {index === 2 && isPracticeDropdownOpen && (
                        <Portal>
                          <div 
                            ref={practiceDropdownRef}
                            className="fixed bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-xl overflow-hidden z-[9999]"
                            style={{
                              width: `${practiceDropdownPosition.width}px`,
                              maxHeight: 'calc(100vh - 200px)',
                              overflowY: 'auto',
                              top: `${practiceDropdownPosition.top}px`,
                              left: `${practiceDropdownPosition.left}px`,
                              right: 'auto'
                            }}
                            onMouseLeave={() => setIsPracticeDropdownOpen(false)}
                          >
                            {practiceMenuItems.map(({ icon: MenuIcon, label: menuLabel, path: menuPath, highlight }) => (
                              <Link
                                key={menuPath}
                                to={menuPath}
                                className={`flex items-center gap-3 px-4 py-3 hover:bg-orange-50 active:bg-orange-100 cursor-pointer transition-colors duration-200
                                  ${location.pathname === menuPath ? 'text-orange-600 bg-orange-50' : highlight ? 'text-orange-500 font-semibold' : 'text-gray-700'}`}
                                onClick={() => setIsPracticeDropdownOpen(false)}
                              >
                                <MenuIcon size={18} className={highlight ? 'text-orange-500' : ''} />
                                <span>{highlight ? menuLabel : menuLabel}</span>
                              </Link>
                            ))}
                          </div>
                        </Portal>
                      )}
                    </li>
                  ))}
                  
                  {/* Desktop More Menu */}
                  <li className="relative">
                    <button
                      ref={desktopMoreButtonRef}
                      className={`flex items-center gap-2 transition-all duration-300 hover:text-orange-600 font-medium relative group
                        ${isDesktopMoreMenuOpen 
                          ? 'text-orange-600 scale-105' 
                          : isHomePage && !isScrolled ? 'text-gray-800' : 'text-gray-700'}`}
                      aria-label="More options"
                      aria-expanded={isDesktopMoreMenuOpen}
                      onMouseEnter={() => {
                        // Change from click to hover
                        if (window.innerWidth >= 768) {
                          setIsDesktopMoreMenuOpen(true);
                          // Close other dropdowns when hovering More
                          setIsLearnDropdownOpen(false);
                          setIsPracticeDropdownOpen(false);
                        }
                      }}
                    >
                      <MoreHorizontal size={20} className={isDesktopMoreMenuOpen ? 'text-orange-600' : ''} /> 
                      More
                      <span className={`absolute -bottom-2 left-0 w-full h-0.5 bg-gradient-to-r from-orange-400 to-red-400 transform scale-x-0 transition-transform group-hover:scale-x-100 ${isDesktopMoreMenuOpen ? 'scale-x-100' : ''}`}></span>
                    </button>
                    
                    {/* Desktop More Menu Dropdown */}
                    {isDesktopMoreMenuOpen && (
                      <Portal>
                        <div 
                          ref={desktopMoreMenuRef}
                          className="fixed bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-xl overflow-hidden z-[9999]"
                          style={{
                            width: `${desktopMenuPosition.width}px`,
                            maxHeight: 'calc(100vh - 200px)',
                            overflowY: 'auto',
                            top: `${desktopMenuPosition.top}px`,
                            left: `${desktopMenuPosition.left}px`,
                            right: 'auto'
                          }}
                          onMouseLeave={() => setIsDesktopMoreMenuOpen(false)}
                        >
                          {moreMenuItems.filter(item => item.path !== '/support/').map(({ icon: Icon, label, path, highlight }) => (
                            <Link
                              key={path}
                              to={path}
                              className={`flex items-center gap-3 px-4 py-3 hover:bg-orange-50 active:bg-orange-100 cursor-pointer transition-colors duration-200
                                ${location.pathname === path ? 'text-orange-600 bg-orange-50' : highlight ? 'text-orange-500 font-semibold' : 'text-gray-700'}`}
                              onClick={() => setIsDesktopMoreMenuOpen(false)}
                            >
                              <Icon size={18} className={highlight ? 'text-orange-500' : ''} />
                              <span>{highlight ? label : label}</span>
                            </Link>
                          ))}
                        </div>
                      </Portal>
                    )}
                  </li>
                  
                  {/* Support Us Button */}
                  <li>
                    <Link to="/support/">
                      <AccentButton className="py-1.5 px-3 text-sm" aria-label="Support Us">
                        <HeartHandshake size={16} />
                        Support Us
                      </AccentButton>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Mobile search (full width when open) */}
        {isSearchOpen && (
          <div className="fixed inset-x-0 top-0 p-4 bg-gradient-to-r from-orange-50 via-orange-100/70 to-orange-50 backdrop-blur-md z-40 border-b border-orange-200/30">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsSearchOpen(false)}
                className="p-2 rounded-full hover:bg-orange-200/50"
                aria-label="Close search"
              >
                <X size={20} />
              </button>
              <div className="flex-1">
                <SearchInput biases={biases} onBiasSelect={handleBiasSelect} />
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}; 